import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Lightbulb, HelpCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { challengeLabOnboardingSteps, challengeLabOnboardingBenefits } from '@/data/onboardingConfigs';
import { ChallengeHistoryPanel } from '@/components/challenge-lab/ChallengeHistoryPanel';
import { ChallengeAssistantPanel } from '@/components/challenge-lab/ChallengeAssistantPanel';
import { AddChallengeDialog } from '@/components/challenge-lab/AddChallengeDialog';
import { AddSolutionDialog } from '@/components/challenge-lab/AddSolutionDialog';
import { SolutionDetailDialog } from '@/components/challenge-lab/SolutionDetailDialog';
import { ChallengeInfoModal } from '@/components/challenge-lab/ChallengeInfoModal';
import { SolutionsListModal } from '@/components/challenge-lab/SolutionsListModal';
import { NPSSurveyModal } from '@/components/NPSSurveyModal';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useAuth } from '@/contexts/AuthContext';
import {
  DbChallenge,
  DbChallengeSolution,
  useChallenges,
  useAllSolutions,
  useCreateChallenge,
  useUpdateChallenge,
  useDeleteChallenge,
  useCreateSolution,
  useConvertToProject,
} from '@/hooks/useChallengeLab';

export default function ChallengeLabPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPaidAccess } = useUserAccess();
  
  const { data: challenges = [], isLoading: challengesLoading } = useChallenges();
  const { data: allSolutions = [] } = useAllSolutions();
  const createChallenge = useCreateChallenge();
  const updateChallenge = useUpdateChallenge();
  const deleteChallenge = useDeleteChallenge();
  const createSolution = useCreateSolution();
  const convertToProject = useConvertToProject();

  const FREE_USER_CHALLENGE_LIMIT = 1;
  const isFreeUser = !hasPaidAccess;
  const hasReachedFreeLimit = isFreeUser && challenges.length >= FREE_USER_CHALLENGE_LIMIT;

  const [activeChallengeId, setActiveChallengeId] = useState<string | null>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  useEffect(() => {
    if (hasAutoSelected || activeChallengeId || challengesLoading) return;
    if (challenges.length > 0) {
      setActiveChallengeId(challenges[0].id);
      setHasAutoSelected(true);
    } else if (!createChallenge.isPending) {
      createChallenge.mutateAsync({ title: 'Nuevo reto', status: 'draft' })
        .then(c => { setActiveChallengeId(c.id); setHasAutoSelected(true); })
        .catch(() => {});
    }
  }, [challenges, hasAutoSelected, activeChallengeId, challengesLoading]);
  
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('challenge_lab_onboarding_seen');
  });
  const [showAddChallenge, setShowAddChallenge] = useState(false);
  const [showAddSolution, setShowAddSolution] = useState(false);
  const [viewingSolution, setViewingSolution] = useState<DbChallengeSolution | null>(null);
  const [showChallengeInfo, setShowChallengeInfo] = useState(false);
  const [showSolutionsList, setShowSolutionsList] = useState(false);
  // Proyecto recién creado sin cohorte (usuario suelto) → se le pregunta NPS
  // antes de navegar a su proyecto.
  const [pendingNpsProject, setPendingNpsProject] = useState<{ id: string } | null>(null);

  const activeChallenge = challenges.find(c => c.id === activeChallengeId) || null;
  const activeChallengeSolutions = activeChallengeId 
    ? allSolutions.filter(s => s.challenge_id === activeChallengeId)
    : [];

  const handleOnboardingComplete = () => {
    localStorage.setItem('challenge_lab_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  const handleCreateDraftChallenge = async () => {
    try {
      const newChallenge = await createChallenge.mutateAsync({
        title: 'Nuevo reto',
        status: 'draft',
      });
      setActiveChallengeId(newChallenge.id);
    } catch (err) {
      console.error('Error creating draft challenge:', err);
    }
  };

  const handleUpdateChallenge = (updates: Partial<DbChallenge>) => {
    if (activeChallengeId) {
      updateChallenge.mutate({ id: activeChallengeId, ...updates });
    }
  };

  const handleSaveChallenge = async (data: { title: string; description: string; target_audience: string; kpi_name: string; kpi_base_value: string; kpi_expected_value: string; status: string }) => {
    try {
      const newChallenge = await createChallenge.mutateAsync(data);
      setActiveChallengeId(newChallenge.id);
      toast.success('Reto guardado exitosamente');
    } catch {
      toast.error('Error al guardar el reto');
    }
  };

  const handleDeleteChallenge = async (id: string) => {
    try {
      await deleteChallenge.mutateAsync(id);
      if (activeChallengeId === id) setActiveChallengeId(null);
      toast.success('Reto eliminado');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveSolution = async (solutionData: { challenge_id: string; name: string; solution_type: string; description: string; data_used: string; suggested_tools: string[]; converted_to_project: boolean }) => {
    try {
      await createSolution.mutateAsync(solutionData);
      toast.success('Solución identificada');
    } catch {
      toast.error('Error al guardar la solución');
    }
  };

  const handleConvertToProject = async (solution: DbChallengeSolution) => {
    if (!activeChallenge) return;
    try {
      const project = await convertToProject.mutateAsync({ solution, challenge: activeChallenge, isFreeUser });
      toast.success('Proyecto creado exitosamente');
      setViewingSolution(null);
      if (!project.cohort_id) {
        // Usuario sin cohorte: se le pregunta NPS antes de navegar a su proyecto.
        setPendingNpsProject({ id: project.id });
      } else {
        navigate(`/project/${project.id}`);
      }
    } catch (err: any) {
      if (err?.message === 'FREE_LIMIT_REACHED') {
        toast.error('Has alcanzado el límite de 1 proyecto para usuarios gratuitos. Actualiza tu plan para crear más proyectos.');
      } else {
        toast.error('Error al crear el proyecto');
      }
    }
  };

  const handleNewChallengeFromPanel = async () => {
    if (hasReachedFreeLimit) {
      toast.error('Has alcanzado el límite de 1 reto para usuarios gratuitos. Actualiza tu plan para crear más retos.');
      return;
    }
    try {
      const newChallenge = await createChallenge.mutateAsync({
        title: 'Nuevo reto',
        status: 'draft',
      });
      setActiveChallengeId(newChallenge.id);
    } catch (err) {
      console.error('Error creating new chat:', err);
    }
  };

  const handleAddSolutionFromModal = () => {
    setShowSolutionsList(false);
    if (activeChallengeId) setShowAddSolution(true);
  };

  const handleViewSolutionFromModal = (solution: DbChallengeSolution) => {
    setShowSolutionsList(false);
    setViewingSolution(solution);
  };

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        onComplete={handleOnboardingComplete}
        steps={challengeLabOnboardingSteps}
        benefits={challengeLabOnboardingBenefits}
        ctaText="Comenzar a Identificar Retos"
      />
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col gap-4">
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/challenge')} className="shrink-0" title="Volver a Proyectos">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Laboratorio de Reto y Soluciones</h1>
            <p className="text-sm text-muted-foreground">Identifica oportunidades de alto impacto para innovar con IA</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setShowOnboarding(true)} title="Ver ayuda">
          <HelpCircle className="w-5 h-5" />
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
        <div className="lg:col-span-4 xl:col-span-3 min-h-0">
          <ChallengeHistoryPanel
            challenges={challenges}
            solutions={allSolutions}
            selectedChallengeId={activeChallengeId}
            onSelectChallenge={setActiveChallengeId}
            onNewChallenge={handleNewChallengeFromPanel}
            onDeleteChallenge={handleDeleteChallenge}
            onViewSolution={setViewingSolution}
            newChallengeDisabled={hasReachedFreeLimit}
          />
        </div>
        <div className="lg:col-span-8 xl:col-span-9 min-h-0">
          <ChallengeAssistantPanel
            activeChallenge={activeChallenge}
            onCreateDraftChallenge={handleCreateDraftChallenge}
            onUpdateChallenge={handleUpdateChallenge}
            onViewChallengeInfo={() => setShowChallengeInfo(true)}
            onViewSolutions={() => setShowSolutionsList(true)}
            solutions={activeChallengeSolutions}
          />
        </div>
      </div>

      <AddChallengeDialog open={showAddChallenge} onOpenChange={setShowAddChallenge} onSave={handleSaveChallenge} />

      {activeChallenge && (
        <AddSolutionDialog
          open={showAddSolution}
          onOpenChange={setShowAddSolution}
          challengeId={activeChallenge.id}
          challengeTitle={activeChallenge.title || 'Reto en progreso'}
          onSave={handleSaveSolution}
        />
      )}

      <SolutionDetailDialog
        open={!!viewingSolution}
        onOpenChange={(open) => !open && setViewingSolution(null)}
        solution={viewingSolution}
        challenge={activeChallenge}
        onConvertToProject={handleConvertToProject}
      />

      <ChallengeInfoModal open={showChallengeInfo} onOpenChange={setShowChallengeInfo} challenge={activeChallenge} />

      <SolutionsListModal
        open={showSolutionsList}
        onOpenChange={setShowSolutionsList}
        solutions={activeChallengeSolutions}
        challengeTitle={activeChallenge?.title || 'Reto en progreso'}
        onAddSolution={handleAddSolutionFromModal}
        onViewSolution={handleViewSolutionFromModal}
      />

      {pendingNpsProject && user && (
        <NPSSurveyModal
          isOpen={!!pendingNpsProject}
          onClose={() => {
            const projectId = pendingNpsProject.id;
            setPendingNpsProject(null);
            navigate(`/project/${projectId}`);
          }}
          moduleName="Laboratorio de Retos y Soluciones"
          userId={user.id}
          triggerKey={`challenge_lab_project:${pendingNpsProject.id}`}
          cohortId={null}
        />
      )}
    </div>
  );
}
